import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Two separate sessions: staff (bufl_admin_token, /admin/*) and fan (bufl_fan_token, everything else).
// Both can be active simultaneously — a super_admin can also be logged in as a fan.
const ADMIN_TOKEN_KEY = "bufl_admin_token";
const FAN_TOKEN_KEY = "bufl_fan_token";

const readLS = (k) => {
  try { return localStorage.getItem(k); } catch (_) { return null; }
};
const writeLS = (k, v) => {
  try {
    if (v) localStorage.setItem(k, v);
    else localStorage.removeItem(k);
  } catch (_) { /* ignore */ }
};

// ---- Admin session ----
export const getToken = () => readLS(ADMIN_TOKEN_KEY);
export const setToken = (t) => writeLS(ADMIN_TOKEN_KEY, t);

// ---- Fan session ----
export const getFanToken = () => readLS(FAN_TOKEN_KEY);
export const setFanToken = (t) => writeLS(FAN_TOKEN_KEY, t);

/** Decide which token to attach to a given request based on its URL. */
function pickToken(url) {
  // Everything under /admin needs the staff token.
  if (url && (url.startsWith("/admin/") || url.startsWith("/auth/") || url === "/admin")) {
    return getToken();
  }
  // Fan-scoped endpoints
  if (url && (url.startsWith("/fan/") || url.startsWith("/live/") || url.startsWith("/stripe/"))) {
    return getFanToken();
  }
  // Everywhere else: prefer fan token if available, else fall back to admin token so a
  // logged-in admin can still call authenticated GETs (e.g. /admin/stats via /admin path).
  return getFanToken() || getToken();
}

export const api = axios.create({ baseURL: API, timeout: 30000 });

api.interceptors.request.use((config) => {
  const t = pickToken(config.url || "");
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Only /admin/* pages redirect to /admin/login on 401. Fan 401s bubble up so
// the Live page can render a "please log in" state without a hard redirect.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (
      err?.response?.status === 401 &&
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/admin") &&
      window.location.pathname !== "/admin/login"
    ) {
      setToken(null);
      window.location.href = "/admin/login";
    }
    return Promise.reject(err);
  },
);

export const assetUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}${path}`;
};

export const fetchJson = async (path, params) => {
  const { data } = await api.get(path, { params });
  return data;
};

export const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  if (detail && typeof detail === "object" && typeof detail.error === "string") {
    return detail.error;
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return err?.message || "Une erreur est survenue";
};
